import { S3Client, PutObjectCommand, ListObjectsV2Command, DeleteObjectCommand } from '@aws-sdk/client-s3';
import axios from 'axios';

// Configure AWS S3 Client
const s3Client = new S3Client({
  region: process.env.REACT_APP_AWS_REGION,
  credentials: {
    accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
    secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
  },
});

const BUCKET_NAME = process.env.REACT_APP_S3_BUCKET_NAME;
const API_BASE = 'https://us-central1-digislidesapp.cloudfunctions.net';

/**
 * Upload a file to S3 using a presigned URL (supports large files)
 * @param {File} file - The file to upload
 * @param {string} folderPath - The folder path in S3 (e.g., 'slideconfig/clientmedia/storeId')
 * @returns {Promise<string>} - The S3 URL of the uploaded file
 */
export const uploadToS3 = async (file, folderPath) => {
  try {
    // Sanitize file name and prefix with timestamp for uniqueness
    const timestamp = Date.now();
    const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const fileName = `${timestamp}-${safeName}`;
    const s3Key = `${folderPath}/${fileName}`;

    // 1) Ask backend for a presigned URL to write this object
    // Use the s3GeneratePresignedUrl cloud function (see functions/index.js)
    const { data: presign } = await axios.post(
      `${API_BASE}/s3GeneratePresignedUrl`,
      {
        s3Key,
        operationType: 'write',
        expiresIn: 900, // 15 minutes
      },
      {
        headers: { 'Content-Type': 'application/json' },
      }
    );

    const uploadUrl = presign.presignedUrl || presign.url;
    if (!uploadUrl) {
      throw new Error('No presigned URL returned from s3GeneratePresignedUrl');
    }

    // 2) Upload file directly to S3 via the presigned URL (no size limit from Cloud Functions)
    const putResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
      body: file,
    });

    if (!putResponse.ok) {
      throw new Error(`S3 upload failed with status ${putResponse.status}`);
    }

    // 3) Construct final public URL
    const publicUrl =
      presign.url ||
      uploadUrl.split('?')[0] ||
      `https://${BUCKET_NAME}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${s3Key}`;

    return publicUrl;
  } catch (error) {
    console.error('Error uploading to S3:', error);
    throw new Error(`Failed to upload file: ${error.message}`);
  }
};

/**
 * List all files in a specific S3 folder
 * @param {string} folderPath - The folder path in S3
 * @returns {Promise<Array>} - Array of file objects
 */
export const getFilesFromS3 = async (folderPath) => {
  try {
    const command = new ListObjectsV2Command({
      Bucket: BUCKET_NAME,
      Prefix: folderPath,
    });

    const response = await s3Client.send(command);

    if (!response.Contents || response.Contents.length === 0) {
      return [];
    }

    const files = response.Contents.map((item) => ({
      key: item.Key,
      size: item.Size,
      lastModified: item.LastModified,
      url: `https://${BUCKET_NAME}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${item.Key}`,
    }));

    return files;
  } catch (error) {
    console.error('Error listing files from S3:', error);
    throw new Error(`Failed to list files: ${error.message}`);
  }
};

/**
 * Delete a file from S3 via cloud function
 * @param {string} fileKey - The S3 key of the file to delete
 * @returns {Promise<void>}
 */
export const deleteFromS3 = async (fileKey) => {
  try {
    // Use uploadApi cloud function endpoint
    const { data: result } = await axios.delete(
      `${API_BASE}/uploadApi/delete`,
      {
        params: { key: fileKey },
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      }
    );

    console.log(`Successfully deleted file: ${fileKey}`, result);
  } catch (error) {
    console.error('Error deleting from S3:', error);
    throw new Error(`Failed to delete file: ${error.message}`);
  }
};

/**
 * Extract S3 key from S3 URL
 * @param {string} s3Url - Full S3 URL
 * @returns {string} - S3 key
 */
export const extractS3KeyFromUrl = (s3Url) => {
  try {
    const url = new URL(s3Url);
    // Remove leading slash
    return url.pathname.substring(1);
  } catch (error) {
    console.error('Error extracting S3 key from URL:', error);
    return null;
  }
};

/**
 * Upload file with progress tracking (for future implementation)
 * @param {File} file - The file to upload
 * @param {string} folderPath - The folder path in S3
 * @param {function} onProgress - Progress callback function
 * @returns {Promise<string>} - The S3 URL
 */
export const uploadToS3WithProgress = async (file, folderPath, onProgress) => {
  // For now, use the standard upload
  // In production, consider using AWS SDK's upload manager or multipart upload
  if (onProgress) {
    onProgress(0);
  }

  const result = await uploadToS3(file, folderPath);

  if (onProgress) {
    onProgress(100);
  }

  return result;
};

/**
 * Read slideconfig.json from S3 via presigned URL
 * @param {string} storeId - Store ID
 * @param {string} tvId - TV ID
 * @returns {Promise<Array>} - Array of slide config objects
 */
export const readSlideConfig = async (storeId, tvId) => {
  const s3Key = `slides/${storeId}/${tvId}/slideconfig.json`;

  try {
    // 1) Get presigned URL for READ
    const { data: presign } = await axios.post(
      `${API_BASE}/s3GeneratePresignedUrl`,
      {
        s3Key,
        operationType: 'read',
        expiresIn: 900,
      },
      { headers: { 'Content-Type': 'application/json' } }
    );

    const url = presign.presignedUrl || presign.url;
    if (!url) {
      throw new Error('No presigned URL returned for reading slideconfig.json');
    }

    // 2) Read JSON directly from S3
    const resp = await axios.get(url, {
      headers: { 'Cache-Control': 'no-cache' },
    });

    if (resp.status !== 200 || !resp.data) {
      return [];
    }

    const data = resp.data;
    return Array.isArray(data) ? data : [];
  } catch (error) {
    // If file doesn't exist or any other error, treat as empty config
    console.warn('readSlideConfig failed (using empty array):', error.message);
    return [];
  }
};

/**
 * Update slideconfig.json in S3 with new media object
 * @param {string} storeId - Store ID
 * @param {string} tvId - TV ID
 * @param {object} mediaObject - Media object to add
 * @returns {Promise<void>}
 */
export const updateSlideConfig = async (storeId, tvId, mediaObject) => {
  const s3Key = `slides/${storeId}/${tvId}/slideconfig.json`;

  // 1) Read existing config
  const existingConfig = await readSlideConfig(storeId, tvId);
  const updatedConfig = [...existingConfig, mediaObject];

  try {
    // 2) Get presigned URL for WRITE
    const { data: presign } = await axios.post(
      `${API_BASE}/s3GeneratePresignedUrl`,
      {
        s3Key,
        operationType: 'write',
        expiresIn: 900,
      },
      { headers: { 'Content-Type': 'application/json' } }
    );

    const url = presign.presignedUrl || presign.url;
    if (!url) {
      throw new Error('No presigned URL returned for writing slideconfig.json');
    }

    // 3) PUT JSON directly to S3
    await axios.put(url, updatedConfig, {
      headers: { 'Content-Type': 'application/json' },
    });

    console.log('Successfully updated slideconfig.json');
  } catch (error) {
    console.error('Error updating slideconfig:', error);
    throw new Error(`Failed to update slideconfig: ${error.message}`);
  }
};
export const updateSlideConfig2 = async (storeId, tvId, mediaObject) => {
  const s3Key = `slides/${storeId}/${tvId}/slideconfig.json`;
  
  try {
    // 2) Get presigned URL for WRITE
    const { data: presign } = await axios.post(
      `${API_BASE}/s3GeneratePresignedUrl`,
      {
        s3Key,
        operationType: 'write',
        expiresIn: 900,
      },
      { headers: { 'Content-Type': 'application/json' } }
    );

    const url = presign.presignedUrl || presign.url;
    if (!url) {
      throw new Error('No presigned URL returned for writing slideconfig.json');
    }

    // 3) PUT JSON directly to S3
    await axios.put(url, mediaObject, {
      headers: { 'Content-Type': 'application/json' },
    });

    console.log('Successfully updated slideconfig.json');
  } catch (error) {
    console.error('Error updating slideconfig:', error);
    throw new Error(`Failed to update slideconfig: ${error.message}`);
  }
};

/**
 * Read canvas.json from S3 via presigned URL
 * @param {string} storeId - Store ID
 * @returns {Promise<Array>} - Array of canvas config objects
 */
export const readCanvasConfig = async (storeId) => {
  const s3Key = `slideconfig/clientcanvas/${storeId}/canvas.json`;

  try {
    // 1) Get presigned URL for READ
    const { data: presign } = await axios.post(
      `${API_BASE}/s3GeneratePresignedUrl`,
      {
        s3Key,
        operationType: 'read',
        expiresIn: 900,
      },
      { headers: { 'Content-Type': 'application/json' } }
    );

    const url = presign.presignedUrl || presign.url;
    if (!url) {
      throw new Error('No presigned URL returned for reading canvas.json');
    }

    // 2) Read JSON directly from S3
    const resp = await axios.get(url, {
      headers: { 'Cache-Control': 'no-cache' },
    });

    if (resp.status !== 200 || !resp.data) {
      return [];
    }

    const data = resp.data;
    return Array.isArray(data) ? data : [];
  } catch (error) {
    // If file doesn't exist or any other error, treat as empty config
    console.warn('readCanvasConfig failed (using empty array):', error.message);
    return [];
  }
};

/**
 * Update canvas.json in S3 with new canvas object
 * If canvasObject has an id, replaces existing entry with same id; otherwise adds as new
 * @param {string} storeId - Store ID
 * @param {object} canvasObject - Canvas object to add or update in the array
 * @returns {Promise<void>}
 */
export const updateCanvasConfig = async (storeId, canvasObject) => {
  const s3Key = `slideconfig/clientcanvas/${storeId}/canvas.json`;

  // 1) Read existing config
  const existingConfig = await readCanvasConfig(storeId);
  
  // 2) Check if canvas has an ID and if it exists in the array
  let updatedConfig;
  if (canvasObject.id) {
    // Replace existing entry with same ID
    const existingIndex = existingConfig.findIndex(item => item.id === canvasObject.id);
    if (existingIndex !== -1) {
      updatedConfig = [...existingConfig];
      updatedConfig[existingIndex] = canvasObject;
      console.log(`Replacing existing canvas with id: ${canvasObject.id}`);
    } else {
      // ID exists but not found in array, add as new
      updatedConfig = [...existingConfig, canvasObject];
      console.log(`Canvas with id ${canvasObject.id} not found, adding as new`);
    }
  } else {
    // No ID, add as new entry
    updatedConfig = [...existingConfig, canvasObject];
    console.log('Adding new canvas (no ID provided)');
  }

  try {
    // 3) Get presigned URL for WRITE
    const { data: presign } = await axios.post(
      `${API_BASE}/s3GeneratePresignedUrl`,
      {
        s3Key,
        operationType: 'write',
        expiresIn: 900,
      },
      { headers: { 'Content-Type': 'application/json' } }
    );

    const url = presign.presignedUrl || presign.url;
    if (!url) {
      throw new Error('No presigned URL returned for writing canvas.json');
    }

    // 4) PUT JSON directly to S3
    await axios.put(url, updatedConfig, {
      headers: { 'Content-Type': 'application/json' },
    });

    console.log('Successfully updated canvas.json');
  } catch (error) {
    console.error('Error updating canvas.json:', error);
    throw new Error(`Failed to update canvas.json: ${error.message}`);
  }
};

/**
 * Delete a canvas from canvas.json in S3
 * @param {string} storeId - Store ID
 * @param {string} canvasId - Canvas ID to delete
 * @returns {Promise<void>}
 */
export const deleteCanvasConfig = async (storeId, canvasId) => {
  const s3Key = `slideconfig/clientcanvas/${storeId}/canvas.json`;

  // 1) Read existing config
  const existingConfig = await readCanvasConfig(storeId);
  
  // 2) Filter out the canvas with the matching ID
  const updatedConfig = existingConfig.filter(item => item.id !== canvasId);

  try {
    // 3) Get presigned URL for WRITE
    const { data: presign } = await axios.post(
      `${API_BASE}/s3GeneratePresignedUrl`,
      {
        s3Key,
        operationType: 'write',
        expiresIn: 900,
      },
      { headers: { 'Content-Type': 'application/json' } }
    );

    const url = presign.presignedUrl || presign.url;
    if (!url) {
      throw new Error('No presigned URL returned for writing canvas.json');
    }

    // 4) PUT JSON directly to S3
    await axios.put(url, updatedConfig, {
      headers: { 'Content-Type': 'application/json' },
    });

    console.log('Successfully deleted canvas from canvas.json');
  } catch (error) {
    console.error('Error deleting canvas:', error);
    throw new Error(`Failed to delete canvas: ${error.message}`);
  }
};

export default {
  uploadToS3,
  getFilesFromS3,
  deleteFromS3,
  extractS3KeyFromUrl,
  uploadToS3WithProgress,
  readSlideConfig,
  updateSlideConfig,
  readCanvasConfig,
  updateCanvasConfig,
  deleteCanvasConfig,
};

